import React, { useState, useEffect, useContext } from "react";
import { getComments, Comment } from "../api-requests/Comments";
import CommentList from "./CommentList";
import CommentComposer from "./CommentComposer";
import { UserContext } from "../context/UserContext";

interface CommentSectionProps {
  targetType: "debate" | "list" | "list_item";
  targetId: string;
  className?: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({
  targetType,
  targetId,
  className = "",
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalComments, setTotalComments] = useState(0);
  const { user } = useContext(UserContext);

  // Fetch comments on component mount
  useEffect(() => {
    fetchComments();
  }, [targetType, targetId]);

  const fetchComments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getComments(targetType, targetId);
      if (response.success && response.comments) {
        setComments(response.comments);
        // Count total comments including replies
        const total = response.comments.reduce(
          (count, comment) => count + 1 + (comment.replies?.length || 0),
          0,
        );
        setTotalComments(total);
      } else {
        setError(response.message || "Failed to load comments");
      }
    } catch (err) {
      setError("An error occurred while loading comments");
      console.error("Error fetching comments:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentAdded = (newComment: Comment) => {
    if (newComment.parentId) {
      // This is a reply, add it to the parent comment's replies
      setComments((prevComments) =>
        prevComments.map((comment) => {
          if (comment.id === newComment.parentId) {
            return {
              ...comment,
              replies: [...(comment.replies || []), newComment],
            };
          }
          return comment;
        }),
      );
    } else {
      // This is a top-level comment
      setComments((prevComments) => [...prevComments, newComment]);
    }
    setTotalComments((prev) => prev + 1);
  };

  const handleCommentUpdated = (updatedComment: Comment) => {
    if (updatedComment.parentId) {
      // Update a reply
      setComments((prevComments) =>
        prevComments.map((comment) => ({
          ...comment,
          replies: comment.replies?.map((reply) =>
            reply.id === updatedComment.id ? updatedComment : reply,
          ),
        })),
      );
    } else {
      // Update a top-level comment
      setComments((prevComments) =>
        prevComments.map((comment) =>
          comment.id === updatedComment.id ? updatedComment : comment,
        ),
      );
    }
  };

  const handleCommentDeleted = (commentId: string) => {
    // Find if it's a top-level comment or reply
    const isTopLevel = comments.some((comment) => comment.id === commentId);

    if (isTopLevel) {
      // Mark top-level comment as deleted (keep structure for replies)
      setComments((prevComments) =>
        prevComments.map((comment) =>
          comment.id === commentId
            ? { ...comment, isDeleted: true, body: "Comment deleted" }
            : comment,
        ),
      );
    } else {
      // Remove reply completely
      setComments((prevComments) =>
        prevComments.map((comment) => ({
          ...comment,
          replies: comment.replies?.filter((reply) => reply.id !== commentId),
        })),
      );
    }
    setTotalComments((prev) => prev - 1);
  };

  if (loading) {
    return (
      <div className={`bg-gray-900/10 rounded-lg p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded mb-4 w-32"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
            <div className="h-4 bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-gray-900/10 border border-gray-700 rounded-lg p-6 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white">
          Comments{" "}
          {totalComments > 0 && (
            <span className="text-gray-400">({totalComments})</span>
          )}
        </h3>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/20 border border-red-600 rounded-lg p-4 mb-6">
          <p className="text-red-400">{error}</p>
          <button
            onClick={fetchComments}
            className="mt-2 text-sm text-red-300 underline hover:text-red-200"
          >
            Try again
          </button>
        </div>
      )}

      {/* Comment Composer - Only show if user is logged in */}
      {user && (
        <CommentComposer
          targetType={targetType}
          targetId={targetId}
          onCommentAdded={handleCommentAdded}
          placeholder="Share your thoughts..."
        />
      )}

      {/* Login prompt for anonymous users */}
      {!user && (
        <div className="bg-gray-700 rounded-lg p-4 mb-6 text-center">
          <p className="text-gray-300 mb-2">Join the discussion!</p>
          <p className="text-sm text-gray-400">
            <a href="/login" className="text-blue-400 hover:text-blue-300">
              Sign in
            </a>{" "}
            or{" "}
            <a href="/register" className="text-blue-400 hover:text-blue-300">
              create an account
            </a>{" "}
            to comment.
          </p>
        </div>
      )}

      {/* Comments List */}
      {comments.length > 0 ? (
        <CommentList
          comments={comments}
          targetType={targetType}
          targetId={targetId}
          onCommentUpdated={handleCommentUpdated}
          onCommentDeleted={handleCommentDeleted}
          onReplyAdded={handleCommentAdded}
        />
      ) : (
        !loading && (
          <div className="text-center py-8">
            <p className="text-gray-400">
              {error ? "Unable to load comments." : "No comments yet."}
            </p>
            {!error && user && (
              <p className="text-sm text-gray-500 mt-2">
                Be the first to share your thoughts!
              </p>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default CommentSection;
